/* eslint-disable */
// Helpers para normalizar resultados Murphy y calcular identidad estable
// del pin. Se mantienen fuera de componentes/store para poder importarse
// desde cualquier lado sin ciclos.

// Placas mexicanas: uppercase, solo alfanumerico. "ABC-123" y "abc 123"
// devuelven "ABC123". Input vacio/null → null.
export function normalizePlaca(raw) {
  if (!raw) return null
  var s = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, '')
  return s || null
}

// Serie (VIN): uppercase alfanumerico. Longitud tipica 12-17.
export function normalizeSerie(raw) {
  if (!raw) return null
  var s = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, '')
  return s || null
}

// CURP: uppercase 18 caracteres. Si no encaja con el regex tipico,
// devuelve null (no vale la pena usarla como id de pin).
export function normalizeCurp(raw) {
  if (!raw) return null
  var s = String(raw).toUpperCase().replace(/\s+/g, '')
  // Los ultimos 2 caracteres son la "homoclave": cada uno puede ser letra o
  // digito. CURPs emitidas antes de ~2005 a veces tienen letra en el ultimo
  // puesto; el regex oficial del RENAPO permite [A-Z0-9] en ambos.
  if (!/^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]{2}$/.test(s)) return null
  return s
}

// Descompone un nombre completo mexicano en {nombre, ap1, ap2} cuando el
// backend no expone apellidos separados. Heurística:
//   - 1 token → solo nombre
//   - 2 tokens → nombre + apellido paterno (sin materno)
//   - 3+ tokens → últimos 2 son paterno+materno, el resto nombres;
//     excepto cuando justo antes del paterno aparece un prefijo de apellido
//     compuesto (DE, DEL, LA, LAS, LOS, SAN, SANTA, VON, VAN, DI, Y):
//     esos tokens se adjuntan al apellido paterno.
// Ejemplos:
//   "JOSE ADRIAN COLIN GUADARRAMA" → {nombre:"JOSE ADRIAN", ap1:"COLIN", ap2:"GUADARRAMA"}
//   "MARIA DE LOS ANGELES CRUZ HERNANDEZ" → {nombre:"MARIA DE LOS ANGELES", ap1:"CRUZ", ap2:"HERNANDEZ"}
//   "JUAN DE LA CRUZ RAMOS" → {nombre:"JUAN", ap1:"DE LA CRUZ", ap2:"RAMOS"}
//   "JUAN PEREZ" → {nombre:"JUAN", ap1:"PEREZ", ap2:""}
// Casos ambiguos con ≤4 tokens y prefijo (ej. "PEDRO DE LA CRUZ") pueden
// descomponerse sub-óptimo — el operador ajusta manualmente en el
// MurphyPanel si hace falta antes de disparar la búsqueda.
export function splitMexicanFullName(full) {
  var out = { nombre: '', ap1: '', ap2: '' }
  if (!full) return out
  var tokens = String(full).trim().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return out
  if (tokens.length === 1) { out.nombre = tokens[0]; return out }
  if (tokens.length === 2) { out.nombre = tokens[0]; out.ap1 = tokens[1]; return out }

  var PREFIXES = {
    DE: 1, DEL: 1, LA: 1, LAS: 1, LOS: 1,
    SAN: 1, SANTA: 1, Y: 1,
    VON: 1, VAN: 1, DI: 1,
  }
  var n = tokens.length
  var apMatStart = n - 1
  var apPatStart = n - 2
  // Extender paterno hacia atrás mientras el token anterior sea un prefijo
  // y quede al menos un token de nombre propio.
  while (apPatStart - 1 >= 1 && PREFIXES[tokens[apPatStart - 1].toUpperCase()]) {
    apPatStart--
  }
  out.nombre = tokens.slice(0, apPatStart).join(' ')
  out.ap1 = tokens.slice(apPatStart, apMatStart).join(' ')
  out.ap2 = tokens[apMatStart]
  return out
}

// Slug determinista para el fallback de persona sin CURP.
// "Juan", "Perez", "Garcia" → "juan-perez-garcia". Ignora acentos, espacios
// extra, puntuacion. Dos busquedas con los mismos tres campos dan el mismo
// slug → mismo pin.
export function slugifyQuery(nombre, ap1, ap2) {
  var parts = [nombre || '', ap1 || '', ap2 || '']
  return parts.map(function(s) {
    return String(s)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
  }).filter(Boolean).join('-') || 'sin-query'
}

// Formatea placa cruda a "ABC-123" para display. Aplica solo al formato
// clasico (3 letras + 3 digitos + letra opcional). Otros formatos validos
// (ej. 2 letras + 4 digitos + letra de EdoMex) hacen fallback al raw
// sin guion, lo cual es aceptable para display.
export function formatPlaca(normalized) {
  if (!normalized) return ''
  var m = String(normalized).match(/^([A-Z]{3})(\d{3})([A-Z0-9])?$/)
  if (m) return m[1] + '-' + m[2] + (m[3] || '')
  return normalized
}

export function formatNombreCompleto(persona) {
  if (!persona) return 'Persona'
  var parts = [persona.nombre, persona.apellido_1, persona.apellido_2]
  var s = parts.filter(Boolean).join(' ').trim()
  return s || 'Persona'
}

// Construye la identidad estable + los queryParams del pin a partir del
// resultado crudo que emite MurphyPanel via 'murphy-result'.
//
// Shape de entrada (de MurphyPanel):
//   { type: 'vehiculo', data: {...}, lprData: [...] }
//   { type: 'persona', personas: [{...}, {...}] }
//
// Shape de salida:
//   { id, kind, title, queryName, queryParams, data|personas, lprData, fetchedAt, status }
//
// Nunca retorna null — si el resultado no tiene placa ni serie ni nombre,
// arroja. El caller (MapV2.onMurphyResult) decide que hacer (hoy: ignorar).
export function normalizeMurphyResult(rawResult) {
  var now = Date.now()
  if (!rawResult || !rawResult.type) {
    throw new Error('normalizeMurphyResult: result sin type')
  }

  if (rawResult.type === 'vehiculo') {
    var d = rawResult.data || {}
    var placa = normalizePlaca(d.placa)
    var serie = normalizeSerie(d.serie)
    // `roboData` y `placasAsociadas` viajan en memoria junto al result.
    // No se persisten en localStorage como tampoco los demás `data` del
    // pin (LFPDPPP) — el store de pines guarda solo {id, kind, title,
    // queryName, queryParams}; ambos campos son derivables al re-fetch.
    var roboData = rawResult.roboData || null
    var placasAsociadas = rawResult.placasAsociadas || []
    if (placa) {
      return {
        id: 'murphy-v-placa-' + placa,
        kind: 'vehiculo',
        title: formatPlaca(placa),
        queryName: 'VehiculosPlaca',
        queryParams: { placa: placa },
        data: d,
        roboData: roboData,
        placasAsociadas: placasAsociadas,
        lprData: rawResult.lprData || [],
        personas: [],
        fetchedAt: now,
        status: 'fresh',
      }
    }
    if (serie) {
      return {
        id: 'murphy-v-serie-' + serie,
        kind: 'vehiculo',
        title: 'Serie ' + serie,
        queryName: 'VehiculosSerie',
        // MurphyService acepta la key 'placa' incluso cuando la query es
        // por VIN (ver MurphyPanel._buscar). Mantenemos el mismo nombre
        // para que refetchMurphyResult pueda reusar estos params
        // directamente con createQuery sin traducir keys.
        queryParams: { placa: serie },
        data: d,
        roboData: roboData,
        placasAsociadas: placasAsociadas,
        lprData: rawResult.lprData || [],
        personas: [],
        fetchedAt: now,
        status: 'fresh',
      }
    }
    throw new Error('normalizeMurphyResult: vehiculo sin placa ni serie')
  }

  if (rawResult.type === 'persona') {
    var personas = rawResult.personas || []
    var p = personas[0] || {}
    var curp = p.ine && p.ine.curp ? normalizeCurp(p.ine.curp) : null
    // queryParams siempre guarda los campos de la busqueda original
    // (nombre, apellidos) — esos son la query que el operador puede
    // re-ejecutar contra Murphy.
    var qp = {
      nombre: (p.nombre || '').trim(),
      apellido_1: (p.apellido_1 || '').trim(),
      apellido_2: (p.apellido_2 || '').trim(),
    }
    if (curp) {
      return {
        id: 'murphy-p-curp-' + curp,
        kind: 'persona',
        title: formatNombreCompleto(p),
        queryName: 'Personas',
        queryParams: qp,
        data: null,
        lprData: [],
        personas: personas,
        fetchedAt: now,
        status: 'fresh',
      }
    }
    var slug = slugifyQuery(p.nombre, p.apellido_1, p.apellido_2)
    return {
      id: 'murphy-p-q-' + slug,
      kind: 'persona',
      title: formatNombreCompleto(p),
      queryName: 'Personas',
      queryParams: qp,
      data: null,
      lprData: [],
      personas: personas,
      fetchedAt: now,
      status: 'fresh',
    }
  }

  throw new Error('normalizeMurphyResult: type desconocido: ' + rawResult.type)
}

// Formatea "hace Xm" / "hace Xh" / "hace Nd". Si ts es null/invalido → ''.
export function formatTimeAgo(ts) {
  if (!ts) return ''
  var diffMs = Date.now() - ts
  if (diffMs < 0 || isNaN(diffMs)) return ''
  var mins = Math.floor(diffMs / 60000)
  if (mins < 1) return 'ahora'
  if (mins < 60) return 'hace ' + mins + 'm'
  var hrs = Math.floor(mins / 60)
  if (hrs < 24) return 'hace ' + hrs + 'h'
  var days = Math.floor(hrs / 24)
  return 'hace ' + days + 'd'
}
